import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type {
  ILibrary,
  IReduxRequestNode,
  IStandardGUJRequestParams,
  IResponseRedirect,
} from "@litres-libs/types";
import { Request } from "../../../request/Request";
import { addHydrateReducer } from "../../../utils/hydrateReducer";

interface ILibrariesState {
  libraries: ILibrary[];
  actions: {
    teleportCSRF: TTeleportCSRFNode;
    unsubscribeCSRF: TUnsubscribeCSRFNode;
    uniteCSRF: TUniteCSRFNode;
    uniteCSRFV2: TUniteCSRFNode;
    getUserLibrariesCSRF: TGetUserLibrariesCSRFNode;
  };
}

type TTeleportCSRFNode = IReduxRequestNode<
  ITeleportGUJRequest,
  ITeleportGUJResponse
>;
type TUnsubscribeCSRFNode = IReduxRequestNode<
  IUnsubscribeGUJRequest,
  IUnsubscribeGUJResponse
>;
type TUniteCSRFNode = IReduxRequestNode<IUniteGUJRequest, IUniteGUJResponse>;
type TGetUserLibrariesCSRFNode = IReduxRequestNode<
  IGetLibrariesGUJRequest,
  IGetLibrariesGUJResponse
>;

const initialState: ILibrariesState = {
  libraries: [],
  actions: {
    teleportCSRF: {},
    unsubscribeCSRF: {},
    uniteCSRF: {},
    uniteCSRFV2: {},
    getUserLibrariesCSRF: {},
  },
};

interface ITeleportGUJParams {
  host: string;
  new_page?: string;
}
type ITeleportGUJRequest = IStandardGUJRequestParams & ITeleportGUJParams;
interface ITeleportGUJResponse {
  status: string;
  redirect: IResponseRedirect;
  csrf: string;
}
/**
 * Получение токена и возврат ссылки на библиотеку
 */
export const teleportCSRF = createAsyncThunk(
  "pages/personalCabinet/libraryTeleportCSRF",
  async (params: {
    data: ITeleportGUJParams;
    onSuccess?: (success: TTeleportCSRFNode) => void;
    onError?: (error: TTeleportCSRFNode) => void;
  }) => {
    const response = new Request.CSRF<
      ITeleportGUJRequest,
      ITeleportGUJResponse
    >({
      url: "/pages/ajax_empty2/",
      provider: {
        id: "teleport",
      },
      request: {
        method: "POST",
        data: {
          action: "teleport",
          gu_ajax: true,
          ...params.data,
        },
      },
      onSuccess: params.onSuccess,
      onError: params.onError,
    });

    return await response.fetch();
  }
);

interface IUnsubscribeGUJParams {
  puid: number;
}
type IUnsubscribeGUJRequest = IStandardGUJRequestParams & IUnsubscribeGUJParams;
interface IUnsubscribeGUJResponse {
  status: string;
}
/**
 * Отписка от библиотеки
 */
export const unsubscribeCSRF = createAsyncThunk(
  "pages/personalCabinet/libraryUnsubscribeCSRF",
  async (params: {
    data: IUnsubscribeGUJParams;
    onSuccess?: (success: TUnsubscribeCSRFNode) => void;
    onError?: (error: TUnsubscribeCSRFNode) => void;
  }) => {
    const response = new Request.CSRF<
      IUnsubscribeGUJRequest,
      IUnsubscribeGUJResponse
    >({
      url: "/pages/ajax_empty2/",
      provider: {
        id: "delete_biblio_user",
      },
      request: {
        method: "POST",
        data: {
          action: "delete_biblio_user",
          gu_ajax: true,
          ...params.data,
        },
      },
      onSuccess: params.onSuccess,
      onError: params.onError,
    });

    return await response.fetch();
  }
);

interface IUniteGUJParams {
  user_login: string;
  user_passwd: string;
}

interface IUniteGUJResponse {
  status: string;
}
type IUniteGUJRequest = IStandardGUJRequestParams & IUniteGUJParams;

type TUniteCSRFV2Node = IReduxRequestNode<IUniteGUJRequest, IUniteGUJResponse>;

/**
 * Запрос на объединене аккаунтов
 */
export const uniteCSRF = createAsyncThunk(
  "pages/personalCabinet/libraryUniteCSRF",
  async (params: IUniteGUJParams) => {
    const response = new Request.CSRF<IUniteGUJRequest, IUniteGUJResponse>({
      url: "/pages/ajax_empty2/",
      provider: {
        id: "unite_libhouse_users",
      },
      request: {
        method: "POST",
        data: {
          action: "unite_libhouse_users",
          gu_ajax: true,
          ...params,
        },
      },
    });

    return await response.fetch();
  }
);

export const uniteCSRFV2 = createAsyncThunk(
  "pages/personalCabinet/libraryUniteCSRFV2",
  async (params: {
    data: IUniteGUJParams;
    onSuccess?: (success: TUniteCSRFV2Node) => void;
    onError?: (error: TUniteCSRFV2Node) => void;
  }) => {
    const response = new Request.CSRF<IUniteGUJRequest, IUniteGUJResponse>({
      url: "/pages/ajax_empty2/",
      provider: {
        id: "unite_libhouse_users",
      },
      request: {
        method: "POST",
        data: {
          action: "unite_libhouse_users",
          gu_ajax: true,
          ...params.data,
        },
      },
      onSuccess: params.onSuccess,
      onError: params.onError,
    });

    return await response.fetch();
  }
);

type IGetLibrariesGUJRequest = IStandardGUJRequestParams;
interface IGetLibrariesGUJResponse {
  libhouses: ILibrary[];
}

/**
 * Запрос на получение списка пользовательских библиотек
 */
export const getUserLibrariesCSRF = createAsyncThunk(
  "pages/personalCabinet/getUserLibrariesCSRF",
  async () => {
    const response = new Request.CSRF<
      IGetLibrariesGUJRequest,
      IGetLibrariesGUJResponse
    >({
      url: "/pages/personal_cabinet_libraries/?gu_ajax=1&lite=1",
      provider: {
        id: "GET_LIBRARIES_DATA",
      },
      request: {
        method: "GET",
      },
    });

    return await response.fetch();
  }
);

export const userLibrariesSlice = createSlice({
  name: "pages/personalCabinet/library",
  initialState,
  reducers: {
    // TODO: Непонятно для чего, не вызывает запросов
    addLibrary: (state: ILibrariesState, action: PayloadAction<ILibrary>) => {
      state.libraries.push(action.payload);
    },
    setLibraries: (
      state: ILibrariesState,
      action: PayloadAction<ILibrary[]>
    ) => {
      state.libraries = action.payload;
    },
  },
  extraReducers: builder => {
    addHydrateReducer(builder, "pages.personalCabinet.userLibraries");
    /**
     * Обработка загрузки и ответа от teleport
     */
    builder.addCase(teleportCSRF.pending, (state: ILibrariesState) => {
      state.actions.teleportCSRF.isLoading = true;
      state.actions.teleportCSRF.error = undefined;
    });
    builder.addCase(
      teleportCSRF.fulfilled,
      (state: ILibrariesState, action: PayloadAction<TTeleportCSRFNode>) => {
        state.actions.teleportCSRF = action.payload;
        state.actions.teleportCSRF.isLoading = false;

        // TODO: Ошибку точно не надо как-то визуально обработать в интерфейсе?
        if (action.payload.error) {
          state.actions.teleportCSRF.error = action.payload.error;
          return;
        }
      }
    );

    /**
     * Обработка загрузки и ответа при объединении учётных записей
     */
    builder.addCase(uniteCSRF.pending, (state: ILibrariesState) => {
      state.actions.uniteCSRF.isLoading = true;
      state.actions.uniteCSRF.error = undefined;
    });
    builder.addCase(
      uniteCSRF.fulfilled,
      (state: ILibrariesState, action: PayloadAction<TUniteCSRFNode>) => {
        state.actions.uniteCSRF = action.payload;
        state.actions.uniteCSRF.isLoading = false;

        // TODO: Ошибку точно не надо как-то визуально обработать в интерфейсе?
        if (action.payload.error) {
          state.actions.uniteCSRF.error = action.payload.error;
          return;
        }

        if (action.payload.response?.status === "ok") {
          location.reload();
        }
      }
    );

    // Обработка загрузки и ответа при объединении учётных записей v2
    builder.addCase(uniteCSRFV2.pending, (state: ILibrariesState) => {
      state.actions.uniteCSRFV2.isLoading = true;
      state.actions.uniteCSRFV2.error = undefined;
    });
    builder.addCase(
      uniteCSRFV2.fulfilled,
      (state: ILibrariesState, action: PayloadAction<TUniteCSRFV2Node>) => {
        state.actions.uniteCSRFV2 = action.payload;
        state.actions.uniteCSRFV2.isLoading = false;

        // TODO: Ошибку точно не надо как-то визуально обработать в интерфейсе?
        if (action.payload.error) {
          state.actions.uniteCSRFV2.error = action.payload.error;
          return;
        }
      }
    );

    /**
     * Обработка загрузки и ответа при отписке от библиотеки
     */
    builder.addCase(unsubscribeCSRF.pending, (state: ILibrariesState) => {
      state.actions.unsubscribeCSRF.isLoading = true;
      state.actions.unsubscribeCSRF.error = undefined;
    });
    builder.addCase(
      unsubscribeCSRF.fulfilled,
      (state: ILibrariesState, action: PayloadAction<TUnsubscribeCSRFNode>) => {
        state.actions.unsubscribeCSRF = action.payload;
        state.actions.unsubscribeCSRF.isLoading = false;

        // TODO: Ошибку точно не надо как-то визуально обработать в интерфейсе?
        if (action.payload.error) {
          state.actions.unsubscribeCSRF.error = action.payload.error;
          return;
        }

        if (action.payload.response?.status === "ok") {
          state.libraries = state.libraries.filter(
            (lib: ILibrary) =>
              lib.puid !== action.payload.request?.request?.params?.puid
          );
        }
      }
    );

    /**
     * Обработка загрузки и ответа при получении данных о библиотеках
     */
    builder.addCase(getUserLibrariesCSRF.pending, (state: ILibrariesState) => {
      state.actions.getUserLibrariesCSRF.isLoading = true;
      state.actions.getUserLibrariesCSRF.error = undefined;
    });
    builder.addCase(
      getUserLibrariesCSRF.fulfilled,
      (
        state: ILibrariesState,
        action: PayloadAction<TGetUserLibrariesCSRFNode>
      ) => {
        state.actions.getUserLibrariesCSRF = action.payload;
        state.actions.getUserLibrariesCSRF.isLoading = false;

        state.libraries = action.payload.response?.libhouses
          ? action.payload.response.libhouses
          : [];

        // TODO: Ошибку точно не надо как-то визуально обработать в интерфейсе?
        if (action.payload.error) {
          state.actions.getUserLibrariesCSRF.error = action.payload.error;
          return;
        }
      }
    );
  },
});

export const { addLibrary } = userLibrariesSlice.actions;

export default userLibrariesSlice.reducer;
